import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GlobalIncident, ScenarioResponseOption } from '../../types/ScenarioManagement';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { StandardButton, FormField, SelectField, TextAreaField } from '../ui';

interface IncidentFormProps {
  incident?: GlobalIncident | null;
  onSave: (incident: Omit<GlobalIncident, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  mode: 'create' | 'edit';
}

export const IncidentForm: React.FC<IncidentFormProps> = ({
  incident,
  onSave,
  onCancel,
  mode
}) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    icon: '🚨',
    description: '',
    severity: 'medium' as GlobalIncident['severity'],
    category: 'cybersecurity' as GlobalIncident['category'],
    timeLimit: 300,
    potentialImpact: {
      security: '',
      reputation: '',
      financial: '',
      operational: ''
    },
    responseOptions: [] as ScenarioResponseOption[],
    isEnabled: true,
    createdBy: 'User',
    tags: [] as string[]
  });

  const [newOption, setNewOption] = useState<ScenarioResponseOption>({
    id: '',
    label: '',
    description: '',
    impact: {
      security: 0,
      reputation: 0,
      financial: 0,
      operational: 0
    },
    timeRequired: 30
  });

  const [showOptionForm, setShowOptionForm] = useState(false);
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);

  useEffect(() => {
    if (incident && mode === 'edit') {
      setFormData({
        title: incident.title,
        icon: incident.icon,
        description: incident.description,
        severity: incident.severity,
        category: incident.category,
        timeLimit: incident.timeLimit,
        potentialImpact: incident.potentialImpact,
        responseOptions: incident.responseOptions,
        isEnabled: incident.isEnabled,
        createdBy: incident.lastModifiedBy || incident.createdBy,
        tags: incident.tags || []
      });
    }
  }, [incident, mode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (formData.responseOptions.length === 0) {
      toast.error('At least one response option is required');
      return;
    }
    if (!formData.potentialImpact.security || !formData.potentialImpact.reputation || 
        !formData.potentialImpact.financial || !formData.potentialImpact.operational) {
      toast.error('All potential impact fields are required');
      return;
    }

    onSave({
      ...formData,
      isGlobal: true,
      lastModifiedBy: mode === 'edit' ? 'User' : undefined
    });
  };

  const handleAddOption = () => {
    if (!newOption.label.trim() || !newOption.description.trim()) {
      toast.error('Option label and description are required');
      return;
    }

    const optionToAdd = {
      ...newOption,
      id: `option_${Date.now()}`
    };

    if (editingOptionIndex !== null) {
      const updatedOptions = [...formData.responseOptions];
      updatedOptions[editingOptionIndex] = optionToAdd;
      setFormData({ ...formData, responseOptions: updatedOptions });
      setEditingOptionIndex(null);
    } else {
      setFormData({ 
        ...formData, 
        responseOptions: [...formData.responseOptions, optionToAdd] 
      });
    }

    setNewOption({
      id: '',
      label: '',
      description: '',
      impact: {
        security: 0,
        reputation: 0,
        financial: 0,
        operational: 0
      },
      timeRequired: 30
    });
    setShowOptionForm(false);
  };

  const handleEditOption = (index: number) => {
    const option = formData.responseOptions[index];
    setNewOption(option);
    setEditingOptionIndex(index);
    setShowOptionForm(true);
  };

  const handleDeleteOption = (index: number) => {
    const updatedOptions = formData.responseOptions.filter((_, i) => i !== index);
    setFormData({ ...formData, responseOptions: updatedOptions });
  };

  const categories: GlobalIncident['category'][] = ['cybersecurity', 'supply_chain', 'ransomware', 'data_breach', 'physical_security', 'compliance', 'operational', 'natural', 'reputation'];
  const severities: GlobalIncident['severity'][] = ['low', 'medium', 'high', 'critical'];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cybersecurity': return '🛡️';
      case 'supply_chain': return '🔗';
      case 'ransomware': return '🔐';
      case 'data_breach': return '💾';
      case 'physical_security': return '🏢';
      case 'compliance': return '📋';
      case 'operational': return '⚙️';
      case 'natural': return '🌪️';
      case 'reputation': return '📢';
      default: return '🚨';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'critical': return '🔴';
      default: return '⚪';
    }
  };

  const cardClass = `rounded-lg p-6 ${
    isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-slate-200'
  }`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div>
          <h3 className={`text-xl font-light mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {mode === 'create' ? 'Create New Incident' : 'Edit Incident'}
          </h3>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Define the incident details and response options
          </p>
        </div>

        {/* Basic Information */}
        <div className={cardClass}>
          <h4 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Basic Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter incident title"
              required
            />

            <FormField
              label="Icon"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="Enter emoji icon"
            />
          </div>

          <TextAreaField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            placeholder="Describe the incident scenario"
            required
            className="mt-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <SelectField
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as GlobalIncident['category'] })}
              options={categories.map(cat => ({
                value: cat,
                label: `${getCategoryIcon(cat)} ${cat.charAt(0).toUpperCase() + cat.slice(1)}`
              }))}
              required
            />

            <SelectField
              label="Severity"
              value={formData.severity}
              onChange={(e) => setFormData({ ...formData, severity: e.target.value as GlobalIncident['severity'] })}
              options={severities.map(sev => ({
                value: sev,
                label: `${getSeverityIcon(sev)} ${sev.charAt(0).toUpperCase() + sev.slice(1)}`
              }))}
              required
            />

            <FormField
              label="Time Limit (seconds)"
              type="number"
              value={formData.timeLimit}
              onChange={(e) => setFormData({ ...formData, timeLimit: parseInt(e.target.value) || 0 })}
              min="60"
              max="3600"
              required
            />
          </div>
        </div>

        {/* Potential Impact */}
        <div className={cardClass}>
          <h4 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Potential Impact *
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(formData.potentialImpact).map(([key, value]) => (
              <TextAreaField
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={value}
                onChange={(e) => setFormData({
                  ...formData,
                  potentialImpact: {
                    ...formData.potentialImpact,
                    [key]: e.target.value
                  }
                })}
                rows={2}
                placeholder={`Describe ${key} impact`}
                required
              />
            ))}
          </div>
        </div>

        {/* Response Options */}
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <h4 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Response Options * ({formData.responseOptions.length})
            </h4>
            {!showOptionForm && (
              <StandardButton
                type="button"
                onClick={() => setShowOptionForm(true)}
                variant="primary"
                size="small"
              >
                Add Option
              </StandardButton>
            )}
          </div>

          {/* Option Form */}
          {showOptionForm && (
            <div className={`p-4 rounded-lg mb-4 ${
              isDark ? 'bg-white/[0.02] border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <h5 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {editingOptionIndex !== null ? 'Edit Response Option' : 'New Response Option'}
              </h5>
              <div className="space-y-3">
                <FormField
                  label="Option Label"
                  value={newOption.label}
                  onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                  placeholder="Option label"
                  required
                />
                <TextAreaField
                  label="Option Description"
                  value={newOption.description}
                  onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                  placeholder="Option description"
                  rows={2}
                  required
                />
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(newOption.impact).map(([key, value]) => (
                    <FormField
                      key={key}
                      label={key.charAt(0).toUpperCase() + key.slice(1)}
                      type="number"
                      value={value}
                      onChange={(e) => setNewOption({
                        ...newOption,
                        impact: {
                          ...newOption.impact,
                          [key]: parseInt(e.target.value) || 0
                        }
                      })}
                    />
                  ))}
                  <FormField
                    label="Time (min)"
                    type="number"
                    value={newOption.timeRequired}
                    onChange={(e) => setNewOption({ ...newOption, timeRequired: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="flex gap-2">
                  <StandardButton
                    type="button"
                    onClick={handleAddOption}
                    variant="success"
                    size="small"
                  >
                    {editingOptionIndex !== null ? 'Update' : 'Add'}
                  </StandardButton>
                  <StandardButton
                    type="button"
                    onClick={() => {
                      setShowOptionForm(false);
                      setEditingOptionIndex(null);
                      setNewOption({
                        id: '',
                        label: '',
                        description: '',
                        impact: { security: 0, reputation: 0, financial: 0, operational: 0 },
                        timeRequired: 30
                      });
                    }}
                    variant="ghost"
                    size="small"
                  >
                    Cancel
                  </StandardButton>
                </div>
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="space-y-2">
            {formData.responseOptions.map((option, index) => (
              <div
                key={option.id}
                className={`p-3 rounded-lg ${
                  isDark ? 'bg-white/[0.02] border border-white/10' : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {option.label}
                    </h5>
                    <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {option.description}
                    </p>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        S: {option.impact.security}
                      </span>
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        R: {option.impact.reputation}
                      </span>
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        F: {option.impact.financial}
                      </span>
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        O: {option.impact.operational}
                      </span>
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        Time: {option.timeRequired}m
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEditOption(index)}
                      className={`p-1 rounded ${
                        isDark ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteOption(index)}
                      className="p-1 rounded text-red-400 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Status */}
        <div className={cardClass}>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isEnabled}
              onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
              className="mr-3"
            />
            <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>
              Enable this incident globally
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <StandardButton
            type="button"
            onClick={onCancel}
            variant="ghost"
            size="large"
          >
            Cancel
          </StandardButton>
          <StandardButton
            type="submit"
            variant="primary"
            size="large"
          >
            {mode === 'create' ? 'Create Incident' : 'Save Changes'}
          </StandardButton>
        </div>
      </form>
    </motion.div>
  );
};

export default IncidentForm;